import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  ButtonBase,
  LinearProgress,
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import ManagerEditModeToggle from '../shared/ManagerEditModeToggle';
import BonusEditSheet from '../shared/BonusEditSheet';
import ConfirmDeleteDialog from '../shared/ConfirmDeleteDialog';
import BonusAuditMode from './BonusAuditMode';
import { TOKENS, fmtMoney, bonusGameColor } from '../theme';
import { detectDuplicates } from '../shared/duplicateDetection';
import {
  localDecisionsSelector,
  ownerSettingsSelector,
  duplicateScoresSelector,
} from '../../../redux/selectors/employeeSessionV2';
import { recordLocalDecision } from '../../../redux/actions/employeeSessionV2';

// Spec §4 — Bonus Report. Two modes: list (with audit tracker) and audit
// (full-screen, one bonus at a time). Manager Edit Mode hides the audit
// tracker and surfaces edit/add/delete affordances.
export default function BonusDetail({
  session,
  sessionData,
  customers,
  manageBonusList,
  onEditBonus,
  onDeleteBonus,
  onAddBonus,
  onSubmitDecision,
  onAuditComplete,
  // When true, the audit tracker shows even if the owner hasn't turned on
  // "require review every bonus" — used where an audit-verification rule
  // forces a review (e.g. before a reading guy can close the session).
  forceAuditTracker = false,
  // When true, the Manager Edit Mode toggle is hidden entirely — a
  // verification-only surface (e.g. the reading guy auditing from Master
  // Reading). With no toggle, edit mode can never be entered.
  hideEditMode = false,
}) {
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [editSheet, setEditSheet] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [auditIndex, setAuditIndex] = useState(0);
  const [auditOpen, setAuditOpen] = useState(false);

  const decisions = useSelector(localDecisionsSelector);
  const ownerSettings = useSelector(ownerSettingsSelector);
  const duplicateSignatures = useSelector(duplicateScoresSelector);

  const bonuses = useMemo(
    () => (sessionData?.bonuses || []).filter((b) => b.status !== 'deleted'),
    [sessionData]
  );
  const totalAmount = useMemo(
    () => bonuses.reduce((s, b) => s + Number(b.bonusAmount || 0), 0),
    [bonuses]
  );
  const signatureLookup = useMemo(() => {
    const m = {};
    (duplicateSignatures || []).forEach((s) => {
      m[s.bonusId] = s;
    });
    return m;
  }, [duplicateSignatures]);
  const duplicateFlags = useMemo(
    () => detectDuplicates(bonuses, signatureLookup),
    [bonuses, signatureLookup]
  );

  // Scope to bonusIds still present so stale decisions for deleted bonuses
  // don't inflate reviewed/approved/flagged totals.
  const bonusIdSet = new Set(bonuses.map((b) => b.bonusId));
  const bonusDecisions = Object.entries(decisions)
    .filter(
      ([k]) =>
        k.startsWith('BONUS:') && bonusIdSet.has(k.slice('BONUS:'.length))
    )
    .reduce((acc, [k, v]) => {
      acc[k] = v;
      return acc;
    }, {});
  const reviewedCount = Object.keys(bonusDecisions).length;
  const approvedCount = Object.values(bonusDecisions).filter(
    (d) => d === 'APPROVED'
  ).length;
  const flaggedByMgrCount = Object.values(bonusDecisions).filter(
    (d) => d === 'FLAGGED'
  ).length;
  const remainingCount = bonuses.length - reviewedCount;
  const auditComplete = reviewedCount === bonuses.length && bonuses.length > 0;

  const openAudit = (idx) => {
    setAuditIndex(idx);
    setAuditOpen(true);
  };
  const handleDecision = (bonus, decision) => {
    dispatch(recordLocalDecision('BONUS', bonus.bonusId, decision));
    onSubmitDecision(bonus, decision);
  };
  const handleAuditComplete = () => {
    setAuditOpen(false);
    if (onAuditComplete) onAuditComplete('Bonus audit completed');
  };

  const handleSaveEdit = (payload) => {
    if (editSheet.mode === 'add') {
      onAddBonus(payload);
    } else {
      onEditBonus(editSheet.entry, payload);
    }
    setEditSheet(null);
  };
  const handleConfirmDelete = () => {
    if (!confirmDelete) return;
    onDeleteBonus(confirmDelete);
    setConfirmDelete(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1.5 }}>
      {!hideEditMode && (
        <ManagerEditModeToggle
          on={editMode}
          onToggle={() => setEditMode((m) => !m)}
        />
      )}

      {/* Hero summary */}
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.amber200}`,
          borderRadius: 2,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: TOKENS.slate500,
            }}
          >
            Total Bonuses Given
          </Typography>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 700,
              fontFamily: 'monospace',
              color: TOKENS.slate900,
            }}
          >
            {fmtMoney(totalAmount)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TOKENS.slate500, mt: 0.25 }}>
            {bonuses.length} bonuses
          </Typography>
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: TOKENS.amber50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CardGiftcardIcon sx={{ color: TOKENS.amber700, fontSize: 24 }} />
        </Box>
      </Box>

      {editMode && (
        <ButtonBase
          onClick={() => setEditSheet({ mode: 'add', entry: {} })}
          sx={{
            width: '100%',
            py: 1.25,
            background: '#fff',
            border: `2px dashed ${TOKENS.amber300}`,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          <AddIcon sx={{ color: TOKENS.amber700, fontSize: 16 }} />
          <Typography
            sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.amber900 }}
          >
            Add Bonus Entry
          </Typography>
        </ButtonBase>
      )}

      {/* Audit tracker (read mode only) */}
      {!editMode &&
        (ownerSettings?.requireReviewEveryBonus || forceAuditTracker) &&
        bonuses.length > 0 && (
          <Box
            sx={{
              background: auditComplete ? TOKENS.emerald50 : '#fff',
              border: `1px solid ${
                auditComplete ? TOKENS.emerald300 : TOKENS.slate200
              }`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: auditComplete
                      ? TOKENS.emerald600
                      : TOKENS.slate900,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {auditComplete ? (
                    <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />
                  ) : (
                    <VisibilityIcon sx={{ color: '#fff', fontSize: 16 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                    {auditComplete ? 'Audit complete' : 'Audit required'}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: TOKENS.slate500 }}>
                    {reviewedCount} of {bonuses.length} reviewed ·{' '}
                    {remainingCount} remaining
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(reviewedCount / Math.max(1, bonuses.length)) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  mb: 1,
                  background: TOKENS.slate200,
                  '& .MuiLinearProgress-bar': { background: TOKENS.emerald500 },
                }}
              />
              <Grid container spacing={1}>
                <Stat
                  label="Approved"
                  value={approvedCount}
                  color={TOKENS.emerald700}
                />
                <Stat
                  label="Flagged"
                  value={flaggedByMgrCount}
                  color={TOKENS.red700}
                />
                <Stat
                  label="Remaining"
                  value={remainingCount}
                  color={TOKENS.slate700}
                />
              </Grid>
            </Box>
            <Button
              fullWidth
              onClick={() =>
                openAudit(
                  Math.max(
                    0,
                    bonuses.findIndex(
                      (b) => !bonusDecisions[`BONUS:${b.bonusId}`]
                    )
                  )
                )
              }
              endIcon={<ChevronRightIcon />}
              sx={{
                py: 1.5,
                borderRadius: 0,
                background: auditComplete ? TOKENS.emerald700 : TOKENS.slate900,
                color: '#fff',
                justifyContent: 'space-between',
                px: 2,
                '&:hover': {
                  background: auditComplete
                    ? TOKENS.emerald900
                    : TOKENS.slate700,
                },
              }}
            >
              {reviewedCount === 0
                ? 'Start Audit'
                : auditComplete
                ? 'Review Audit'
                : 'Continue Audit'}
            </Button>
          </Box>
        )}

      {/* Duplicate alert */}
      {duplicateFlags.size > 0 && !editMode && (
        <Box
          sx={{
            background: TOKENS.red50,
            border: `1px solid ${TOKENS.red200}`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ color: TOKENS.red600, fontSize: 20 }} />
          <Box>
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.red900 }}
            >
              {duplicateFlags.size} possible duplicate
              {duplicateFlags.size > 1 ? 's' : ''} detected
            </Typography>
            <Typography sx={{ fontSize: 11, color: TOKENS.red700 }}>
              Photos with strong reuse signals — review carefully before
              approving.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Bonus cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {bonuses.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: TOKENS.slate400,
              fontSize: 13,
              background: '#fff',
              border: `1px solid ${TOKENS.slate200}`,
              borderRadius: 2,
            }}
          >
            No bonuses given this session.
          </Box>
        )}
        {bonuses.map((b, idx) => {
          const flag = duplicateFlags.get(b.bonusId);
          const dec = bonusDecisions[`BONUS:${b.bonusId}`];
          const gameColor = bonusGameColor(b.bonusName);
          const photoUrl = b.fullImageUrl || b.imageUrl || b.photo;
          const customer =
            b.customer ||
            [b.firstName, b.lastName].filter(Boolean).join(' ').trim();
          const borderColor =
            dec === 'APPROVED'
              ? TOKENS.emerald300
              : dec === 'FLAGGED'
              ? TOKENS.red300
              : flag?.severity === 'high'
              ? TOKENS.red300
              : flag?.severity === 'medium'
              ? TOKENS.amber300
              : TOKENS.slate200;
          return (
            <Box
              key={b.bonusId}
              sx={{
                background: '#fff',
                border: `1px solid ${borderColor}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {flag && !dec && (
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    background:
                      flag.severity === 'high' ? TOKENS.red50 : TOKENS.amber50,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <WarningAmberIcon
                    sx={{
                      fontSize: 14,
                      color:
                        flag.severity === 'high'
                          ? TOKENS.red700
                          : TOKENS.amber700,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      flex: 1,
                      color:
                        flag.severity === 'high'
                          ? TOKENS.red900
                          : TOKENS.amber900,
                    }}
                    noWrap
                  >
                    {flag.reason}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 10, fontWeight: 700, opacity: 0.8 }}
                  >
                    {flag.confidence}%
                  </Typography>
                </Box>
              )}
              <ButtonBase
                onClick={() =>
                  editMode
                    ? setEditSheet({ mode: 'edit', entry: b, index: idx + 1 })
                    : openAudit(idx)
                }
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: 1,
                  p: 1.25,
                  textAlign: 'left',
                }}
              >
                <Box
                  sx={{
                    width: 54,
                    height: 72,
                    borderRadius: 1,
                    overflow: 'hidden',
                    background: TOKENS.slate100,
                    flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  {photoUrl && (
                    <Box
                      component="img"
                      src={photoUrl}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                  {dec && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background:
                          dec === 'APPROVED'
                            ? TOKENS.emerald600
                            : TOKENS.red600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {dec === 'APPROVED' ? (
                        <CheckIcon sx={{ color: '#fff', fontSize: 12 }} />
                      ) : (
                        <CloseIcon sx={{ color: '#fff', fontSize: 12 }} />
                      )}
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 0.5,
                      mb: 0.25,
                    }}
                  >
                    <Chip
                      size="small"
                      label={b.bonusName}
                      sx={{
                        height: 16,
                        fontSize: 9,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        background: gameColor.bg,
                        color: gameColor.text,
                        border: `1px solid ${gameColor.border}`,
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.25,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 700,
                          fontFamily: 'monospace',
                        }}
                      >
                        ${b.bonusAmount}
                      </Typography>
                      {editMode && (
                        <EditIcon
                          sx={{ color: TOKENS.amber600, fontSize: 12, mt: 0.5 }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Typography
                    sx={{ fontSize: 11, color: TOKENS.slate500 }}
                    noWrap
                  >
                    {b.specify}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 700, mt: 0.25 }}
                    noWrap
                  >
                    {customer || '—'}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 0.5,
                    }}
                  >
                    <Chip
                      size="small"
                      label={`Machine #${b.machine}`}
                      sx={{
                        height: 16,
                        fontSize: 9,
                        fontWeight: 700,
                        background: TOKENS.slate100,
                      }}
                    />
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                    >
                      <AccessTimeIcon
                        sx={{ fontSize: 11, color: TOKENS.slate500 }}
                      />
                      <Typography sx={{ fontSize: 11, color: TOKENS.slate500 }}>
                        {b.time}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ButtonBase>
              {editMode && (
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderTop: `1px solid ${TOKENS.slate100}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setConfirmDelete(b)}
                    sx={{ color: TOKENS.red600, fontSize: 11 }}
                  >
                    Delete
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {editSheet && (
        <BonusEditSheet
          // Force remount each open so useState re-initialises
          // (the Drawer's close transition keeps the component
          // alive otherwise, leaking previous edit state).
          key={`${editSheet.mode}-${editSheet.entry?.bonusId || 'new'}`}
          open
          onClose={() => setEditSheet(null)}
          mode={editSheet.mode}
          entry={editSheet.entry}
          index={editSheet.index}
          customers={customers}
          manageBonusList={manageBonusList}
          onSave={handleSaveEdit}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteDialog
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={handleConfirmDelete}
          title={`Delete Bonus #${bonuses.indexOf(confirmDelete) + 1}?`}
          subtitle={`${confirmDelete.bonusName} · $${confirmDelete.bonusAmount}`}
        />
      )}
      <BonusAuditMode
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        onComplete={handleAuditComplete}
        bonuses={bonuses}
        duplicateFlags={duplicateFlags}
        decisions={decisions}
        auditIndex={auditIndex}
        onIndexChange={setAuditIndex}
        onDecision={handleDecision}
      />
    </Box>
  );
}

function Stat({ label, value, color }) {
  return (
    <Grid item xs={4}>
      <Box
        sx={{
          textAlign: 'center',
          background: TOKENS.slate50,
          borderRadius: 1,
          py: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            fontFamily: 'monospace',
            color,
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontSize: 8,
            fontWeight: 700,
            textTransform: 'uppercase',
            color: TOKENS.slate500,
            mt: 0.25,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Grid>
  );
}
